import { forwardRef, useState, useRef, useEffect, ReactNode } from 'react';

import { cn } from '@foundation/utils/cn';
import { ChevronDownIcon } from '@shared/ui/icons';

export interface SearchableSelectOption<T = string> {
  value: T;
  label: string;
  searchableText?: string;
}

export interface SearchableSelectProps<T = string> {
  options: SearchableSelectOption<T>[];
  name: string;
  disabled?: boolean;
  value?: T;
  displayValue?: string;
  placeholder?: string;
  onChange: (value: T) => void;
  icon?: ReactNode;
  autoComplete?: string;
  styles?: {
    wrapper?: string;
    input?: string;
    dropdown?: string;
    chevron?: string;
  };
  renderOption?: ({
    option,
    isSelected,
    onSelect,
  }: OptionRendererProps<T>) => ReactNode;
  noResultsText: string;
}

type OptionRendererProps<T> = {
  option: SearchableSelectOption<T>;
  isSelected: boolean;
  onSelect: (value: T) => void;
};

const defaultRenderOption = <T,>({
  option,
  isSelected,
  onSelect,
}: OptionRendererProps<T>) => (
  <button
    key={option.value as string}
    type="button"
    onClick={() => onSelect(option.value)}
    className={cn(
      'w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left',
      {
        'bg-secondary': isSelected,
      },
    )}
  >
    <span className={cn('text-primary', { 'font-semibold': isSelected })}>
      {option.label}
    </span>
  </button>
);

function SearchableSelectInner<T = string>(
  {
    options,
    name,
    value,
    displayValue,
    placeholder,
    onChange,
    disabled,
    icon,
    autoComplete,
    renderOption,
    noResultsText,
    styles = {
      wrapper: '',
      input: '',
      dropdown: '',
    },
  }: SearchableSelectProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const selectedOption = options.find((opt) => opt.value === value);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(selectedOption?.label || '');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search query
  const filteredOptions = searchQuery
    ? options.filter((option) => {
        const searchText = option.searchableText || option.label;
        return searchText.toLowerCase().includes(searchQuery.toLowerCase());
      })
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue: T) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div
      className={cn('relative flex items-center', styles.wrapper)}
      ref={dropdownRef}
    >
      {/* Hidden input for form submission */}
      <input
        ref={ref}
        type="hidden"
        name={name}
        value={value !== undefined ? String(value) : ''}
        readOnly
      />

      {icon && <div className="absolute">{icon}</div>}

      <input
        ref={inputRef}
        name="searchQuery"
        type="text"
        value={
          isOpen ? searchQuery : displayValue || selectedOption?.label || ''
        }
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={() => {
          setIsOpen(true);
          setSearchQuery('');
        }}
        disabled={disabled}
        className={cn(
          'min-w-[60px] text-base font-medium focus:outline-none pr-5',
          {
            'cursor-not-allowed': disabled,
            'pl-6': !!icon,
          },
          styles.input,
        )}
      />
      {!isOpen && (
        <ChevronDownIcon
          className={cn(
            'w-4 h-4 text-primary absolute right-0 pointer-events-none',
            styles.chevron,
          )}
        />
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          className={cn(
            'absolute left-0 top-full mt-1 z-20 bg-primary border border-primary rounded-lg shadow-lg max-h-60 overflow-y-auto min-w-[200px]',
            styles.dropdown,
          )}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => {
              const isSelected = value === option.value;

              return renderOption
                ? renderOption({ option, isSelected, onSelect: handleSelect })
                : defaultRenderOption({
                    option,
                    isSelected,
                    onSelect: handleSelect,
                  });
            })
          ) : (
            <div className="px-3 py-2 text-center text-tertiary text-sm">
              {noResultsText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const SearchableSelect = forwardRef(SearchableSelectInner) as <
  T = string,
>(
  props: SearchableSelectProps<T> & {
    ref?: React.ForwardedRef<HTMLInputElement>;
  },
) => ReturnType<typeof SearchableSelectInner>;
